import dlv from 'dlv';
import _ from 'lodash';
import { FieldNamesMarkedBoolean, FieldValues } from 'react-hook-form';
import {
	DateFieldConfiguration,
	GroupFieldConfiguration,
	SelectFieldConfiguration,
} from '../../../api/ConfigApi';
import { MediaResponse } from '../../../types/media';
import {
	FieldGroupCreateRequest,
	FieldGroupUpdateRequest,
} from '../../../types/policy';
import { FIELD_TYPES, PERIL_FIELD_NAMES } from '../data/constants';
import { DirtyFields, FormBuilder, FormShape } from '../types';
import { dateConversion } from './dateConversion';
import flattenObjectByName from './flattenObjectByName';

type FastFormFieldValues = string | string[] | MediaResponse[];

type FastFormGroupFieldValues = {
	[key: string]: FastFormFieldValues;
}[];

const updateRequestBuilder = <T,>(
	dirtyFields: FieldNamesMarkedBoolean<FieldValues>,
	formValues: FieldValues,
	fields: FormShape<FormBuilder>,
	formName: string,
	timezone: string,
	packageAllFields = false,
	isEndorsementOrRenewal = false,
): T => {
	// Build a directory of fields from our deeply nested form
	const defaultValues = fields[formName].values;
	const defaultAddFieldGroups = fields[formName].addFieldGroups;
	const currFields = fields[formName].fields;
	const currFieldsMap = flattenObjectByName(fields[formName].fields, formName);
	const currValues = formValues[formName];
	const currDirtyFields = dirtyFields[formName] || {};

	// find all fields that have conditions
	const conditionsMap = {} as {
		[key: string]: { [fieldName: string]: string[] };
	};
	currFields
		.filter((field) => field.condition)
		.forEach((field) => (conditionsMap[field.name] = field.condition));

	Object.keys(conditionsMap).forEach((fieldName) => {
		const conditions = conditionsMap[fieldName];

		// check that the current value in flatFormValues is not equal to at least one of the possible ok values
		const fieldShown = Object.keys(conditions).some((fieldToCheck) => {
			const requiredConditionalVal = conditions[fieldToCheck];
			const conditionalVals = currValues[fieldToCheck];
			// for repeatable conditional fields, conditionalVals will be array
			return _.isArray(conditionalVals)
				? conditionalVals.includes(requiredConditionalVal[0])
				: requiredConditionalVal.includes(conditionalVals as string);
		});
		if (
			!fieldShown &&
			currValues[fieldName] &&
			!Object.keys(PERIL_FIELD_NAMES).includes(fieldName) // Don't mess with peril fields, which get hidden by pre-configured conditions
		) {
			// if the value is present but no longer shown, remove the condition from the field
			currValues[fieldName] = [];
			currDirtyFields[fieldName] = true;
		}
	});

	const formUpdateObject = {
		fieldValues: {},
	} as any;

	// Flat dirty fields gives us a directory to each field
	currDirtyFields &&
		Object.keys(currDirtyFields).forEach((fieldName) => {
			const fieldPath = [formName, fieldName].join('.');
			const field = currFieldsMap[fieldPath];

			if (!field) {
				return;
			}

			// If the field isn't dirty (changed) or if the field does not exist, we can skip it
			let isDirty = false;
			// An array of dirtyFields means we are dealing with a field group or a media / repeatable select field
			if (Array.isArray(currDirtyFields[fieldName])) {
				if (
					field.type === FIELD_TYPES.select &&
					(field as SelectFieldConfiguration).repeatable
				) {
					if (isEndorsementOrRenewal) {
						isDirty = true;
					} else {
						isDirty = (currDirtyFields[fieldName] as boolean[]).some(
							(dirty) => dirty === true,
						);
					}
				} else {
					currDirtyFields[fieldName].forEach((dirtyFields: DirtyFields) => {
						const hasTrueKeys =
							dirtyFields &&
							Object.keys(dirtyFields).some((k) => dirtyFields[k]);
						if (hasTrueKeys) {
							isDirty = true;
						}
					});
				}
			} else {
				isDirty =
					currDirtyFields[fieldName] ||
					(fieldName in currDirtyFields && currDirtyFields[fieldName] === ''); // SOCA-10456 if the fieldName is in the currDirtyField object but value is '', it has been changed
			}

			if (!packageAllFields && !isDirty) {
				return;
			}
			const fieldValue = currValues[fieldName];
			// If the field does not have fields, it is a standard input field
			if (!(field as GroupFieldConfiguration).fields) {
				if (field.type === FIELD_TYPES.media) {
					formUpdateObject.fieldValues[field.name] = [];

					// only add media with "truthy" media locator
					(fieldValue as MediaResponse[]).forEach((media) => {
						if (media && media.locator) {
							formUpdateObject.fieldValues[field.name].push(media.locator);
						}
					});
				} else if (field.type === FIELD_TYPES.date) {
					let parsedDate = '';
					const dateObject = dateConversion.dateFormStringToDayJs(
						fieldValue as string,
						(field as DateFieldConfiguration).precision,
					);
					if (dateObject) {
						parsedDate = dateConversion.dayJsToLocalizedBackendFormattedString(
							dateObject,
							(field as DateFieldConfiguration).precision,
							timezone,
						);
					} else {
						parsedDate = fieldValue as string;
					}

					formUpdateObject.fieldValues[field.name] =
						parsedDate !== '' ? [parsedDate] : [];
				} else {
					formUpdateObject.fieldValues[field.name] = Array.isArray(fieldValue)
						? (fieldValue as string[])
						: fieldValue
						? [fieldValue]
						: [];
				}
			}
			// A field having 'fields' means it's a field group, so we need to extract the nested form and run this function again
			else {
				(fieldValue as FastFormGroupFieldValues).forEach(
					(groupFieldRowFieldValues, groupFieldRowIndex) => {
						// Group field values are stored in maps
						// fieldName: ['abc', 'def', ...]
						// fieldGroupsByLocator: { 'abc': { 'fieldName': 'fieldValue' }, 'def': { 'fieldName': 'fieldValue' }, ... }
						// By checking for a locator, we can tell if there is an update / create / delete for this row

						const locator = dlv(
							defaultValues,
							[field.name, groupFieldRowIndex].join('.'),
							null,
						) as string | null;

						let groupFieldRowValues = {
							[field.name]: Object.assign(
								{},
								currValues[field.name][groupFieldRowIndex],
							),
						};

						let endorsementRenewalDirtyFields = {
							[field.name]: Object.assign(
								{},
								currValues[field.name][groupFieldRowIndex],
							),
						};

						let groupFieldRowDirtyFields = {
							[field.name]: currDirtyFields[field.name][groupFieldRowIndex],
						};

						const groupFieldRowForm = {
							[field.name]: {
								fields: (field as GroupFieldConfiguration).fields,
								values: groupFieldRowFieldValues,
							} as FormBuilder,
						} as FormShape<FormBuilder>;

						const groupFieldRowUpdate = updateRequestBuilder<T>(
							isEndorsementOrRenewal
								? endorsementRenewalDirtyFields
								: groupFieldRowDirtyFields,
							groupFieldRowValues,
							groupFieldRowForm,
							field.name,
							timezone,
							packageAllFields,
							isEndorsementOrRenewal,
						);

						const fieldValues = (groupFieldRowUpdate as any).fieldValues || {};
						// IF the field group has a locator, it is an update
						if (locator) {
							const fieldGroupUpdate = {
								fieldName: field.name,
								fieldGroupLocator: locator,
								fieldValues,
							} as FieldGroupUpdateRequest;
							if (
								fieldGroupUpdate?.fieldValues &&
								Object.keys(fieldGroupUpdate?.fieldValues).length > 0
							) {
								if (formUpdateObject.updateFieldGroups)
									formUpdateObject.updateFieldGroups.push(fieldGroupUpdate);
								else {
									formUpdateObject.updateFieldGroups = [fieldGroupUpdate];
								}
							}
						}
						// IF the field group does not have a locator, it is a create
						else {
							const fieldGroupCreateRequest = {
								fieldName: field.name,
								fieldValues,
							} as FieldGroupCreateRequest;
							if (formUpdateObject.addFieldGroups) {
								formUpdateObject.addFieldGroups.push(fieldGroupCreateRequest);
							} else {
								formUpdateObject.addFieldGroups = [fieldGroupCreateRequest];
							}
						}
					},
				);
				// IF there are default values associated with this, the field was deleted
				if (defaultValues[field.name]) {
					defaultValues[field.name].forEach((locator, index) => {
						// Check if there is a new field group in the form
						const values = currValues[field.name][index];
						if (!values) {
							if (formUpdateObject.removeFieldGroups) {
								formUpdateObject.removeFieldGroups.push(locator);
							} else {
								formUpdateObject.removeFieldGroups = [locator];
							}
						}
					});
				}
				// IF there are add field groups, we want to do something...
				if (defaultAddFieldGroups && !formUpdateObject.addFieldGroups) {
					if (formUpdateObject.allAddFieldGroupsDeleted) {
						formUpdateObject.allAddFieldGroupsDeleted[fieldName] = true;
					} else {
						formUpdateObject.allAddFieldGroupsDeleted = {
							[fieldName]: true,
						};
					}
				}
			}
		});

	if (Object.keys(formUpdateObject.fieldValues).length === 0) {
		delete formUpdateObject.fieldValues;
	}
	return formUpdateObject;
};

export default updateRequestBuilder;
